import { useForm, router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { FormSection } from '@/components/backoffice/FormSection'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

type TaskTypeOption = { id: string; name: string; code: string }
type BusinessOption = { id: string; name: string; account_id: string; account_name: string }

type Props = {
  types: TaskTypeOption[]
  businesses: BusinessOption[]
}

export default function BusinessTasksNew({ types, businesses }: Props) {
  const { data, setData, processing } = useForm({
    task_type_id: '',
    business_id: '',
    instructions: '',
    due_date: '',
  })

  const selectClass = "flex h-9 w-full items-center rounded-md border border-input bg-transparent pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.post('/backoffice/business_tasks', { business_task: data })
  }

  return (
    <BackofficeLayout
      title="Assign Task"
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Tasks', href: '/backoffice/business_tasks' },
        { label: 'Assign Task' },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Assign Task</h1>
          <p className="text-sm text-muted-foreground">Assign a task to a business</p>
        </div>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
          <FormSection title="Task Assignment">
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Task Type</label>
              <select value={data.task_type_id} onChange={e => setData('task_type_id', e.target.value)} className={selectClass}>
                <option value="">Select task type...</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Business</label>
              <select value={data.business_id} onChange={e => setData('business_id', e.target.value)} className={selectClass}>
                <option value="">Select business...</option>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name} ({b.account_name})</option>)}
              </select>
            </div>
          </FormSection>

          <FormSection title="Details">
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Instructions (optional)</label>
              <Textarea className="text-sm min-h-15" value={data.instructions} onChange={e => setData('instructions', e.target.value)} placeholder="Specific instructions for this task..." />
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Due Date (optional)</label>
              <Input className="h-8 text-sm" type="date" value={data.due_date} onChange={e => setData('due_date', e.target.value)} />
            </div>
          </FormSection>

          <div className="flex gap-2 pt-2">
            <Button type="submit" size="sm" disabled={processing || !data.task_type_id || !data.business_id} className="h-8 text-[13px]">Assign Task</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => window.history.back()} className="h-8 text-[13px]">Cancel</Button>
          </div>
        </form>
      </div>
    </BackofficeLayout>
  )
}
